import { useEffect, useState, useCallback, Fragment } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { Download, FileText, Loader2, RefreshCw } from "lucide-react";
import { money } from "./shared";

export function Materials({ reference }) {
  const [wastage, setWastage] = useState(10);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includePrices, setIncludePrices] = useState(false);

  const load = useCallback(async (w) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/booking/${reference}/materials`, { params: { wastage: w } });
      setData(r.data);
    } catch { toast.error("Could not load materials"); }
    setLoading(false);
  }, [reference]);

  useEffect(() => { load(10); }, [load]);

  const base = api.defaults.baseURL;
  const showCosts = !!data?.show_costs;
  const exportUrl = (ext) => `${base}/admin/booking/${reference}/materials.${ext}?wastage=${wastage}${includePrices ? "&prices=1" : ""}`;

  return (
    <div data-testid="jobfile-materials">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="label-eyebrow">Materials estimate</div>
          <p className="text-xs text-[#6B7280] mt-1 max-w-md">Indicative quantities from the configured spec. Confirm on site before ordering.{showCosts ? " Costs show our buy price vs the market benchmark." : ""}</p>
        </div>
        <div className="flex items-end gap-2">
          <label className="text-xs text-[#6B7280]">Wastage %
            <input data-testid="materials-wastage" type="number" value={wastage} min={0} max={50}
              onChange={e => setWastage(e.target.value)} className="border border-[#E5E7EB] px-2 py-1 text-sm w-20 block mt-1" />
          </label>
          <button data-testid="materials-apply" onClick={() => load(wastage)} className="de-btn-secondary text-xs"><RefreshCw size={13} /> Apply</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <a data-testid="materials-csv" href={exportUrl("csv")} target="_blank" rel="noreferrer" className="de-btn-secondary text-xs inline-flex items-center gap-1"><Download size={13} /> CSV</a>
        <a data-testid="materials-pdf" href={exportUrl("pdf")} target="_blank" rel="noreferrer" className="de-btn-secondary text-xs inline-flex items-center gap-1"><FileText size={13} /> PDF</a>
        {showCosts && (
          <label className="flex items-center gap-2 text-xs text-[#6B7280] cursor-pointer">
            <input data-testid="materials-include-prices" type="checkbox" checked={includePrices} onChange={e => setIncludePrices(e.target.checked)} />
            Include prices in export <span className="text-[#9CA3AF]">(senior admin only)</span>
          </label>
        )}
      </div>

      {loading && <div className="flex items-center gap-2 text-sm text-[#6B7280] py-6"><Loader2 size={16} className="animate-spin" /> Loading…</div>}

      {!loading && data && (
        <>
          <div className="overflow-x-auto border border-[#E5E7EB]">
            <table className="w-full text-sm" data-testid="materials-table">
              <thead>
                <tr className="bg-[#12263A] text-white text-left text-xs uppercase tracking-wide">
                  <th className="px-3 py-2">Material</th>
                  <th className="px-3 py-2">Unit</th>
                  <th className="px-3 py-2 text-right">Qty (+{Math.round(data.wastage_pct)}%)</th>
                  {showCosts && <th className="px-3 py-2 text-right">Our £</th>}
                  {showCosts && <th className="px-3 py-2 text-right">Market £</th>}
                </tr>
              </thead>
              <tbody>
                {(data.sections || []).map((sec, si) => (
                  <Fragment key={sec.name}>
                    <tr data-testid={`materials-section-${si}`} className="bg-[#F8F6F2]">
                      <td colSpan={showCosts ? 5 : 3} className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-[#12263A]">{sec.name}</td>
                    </tr>
                    {sec.rows.map((r, i) => (
                      <tr key={`${r.name}-${r.unit}`} data-testid={`material-row-${si}-${i}`} className="border-t border-[#E5E7EB]">
                        <td className="px-3 py-2">{r.name}{r.note ? <span className="block text-[11px] text-[#9CA3AF]">{r.note}</span> : null}</td>
                        <td className="px-3 py-2 text-[#6B7280]">{r.unit}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-bold">{r.qty_with_wastage}</td>
                        {showCosts && <td className="px-3 py-2 text-right tabular-nums">{money(r.our_total)}</td>}
                        {showCosts && <td className="px-3 py-2 text-right tabular-nums text-[#6B7280]">{money(r.market_total)}</td>}
                      </tr>
                    ))}
                    {showCosts && (
                      <tr className="border-t border-[#E5E7EB] bg-[#FAFAFA] text-xs">
                        <td className="px-3 py-1.5 font-bold text-[#12263A]" colSpan={3}>{sec.name} subtotal</td>
                        <td className="px-3 py-1.5 text-right tabular-nums font-bold">{money(sec.our_total)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-[#6B7280]">{money(sec.market_total)}</td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {(!data.sections || data.sections.length === 0) && (
                  <tr><td colSpan={showCosts ? 5 : 3} className="px-3 py-6 text-center text-[#6B7280]">No spec captured for this job, so no materials could be estimated.</td></tr>
                )}
              </tbody>
              {showCosts && data.sections?.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-[#12263A] font-black">
                    <td className="px-3 py-2" colSpan={3}>Estimated materials total</td>
                    <td className="px-3 py-2 text-right tabular-nums" data-testid="materials-total-our">{money(data.totals.our)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[#6B7280]" data-testid="materials-total-market">{money(data.totals.market)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {!showCosts && <p className="text-xs text-[#9CA3AF] mt-3">Costs and expenses are available to senior administrators.</p>}
        </>
      )}
    </div>
  );
}
